<template>
  <ul
    v-if="isOpen && filteredItems.length"
    ref="optionsListRef"
    v-on-click-outside="closeAndClear"
    data-cy="formula-dropdown"
  >
    <formula-dropdown-item
      v-for="(option, index) in filteredItems"
      :key="option.name + index"
      class="cursor-pointer overflow-auto rounded-sm py-2 hover:bg-gray-50"
      :class="{ 'bg-gray-100': index === highlightedIndex }"
      :option="option"
      :needle="searchTerm"
      @mousedown.prevent="select(option)"
    />
    <li
      v-show="foundItemsCount > filteredItems.length"
      class="rounded-sm p-2 text-sm text-disabled-medium"
    >
      {{ moreItemsCountText }}
    </li>
  </ul>
</template>
<script setup lang="ts">
import { onKeyStroke } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { EditorNodeName } from "@/enums/customAttributes";
import { useAttributeBuilderServices } from "@/hooks/attributeBuilder";
import { useSearchSelectProvider } from "@/hooks/searchSelectProvider";

import FormulaDropdownItem from "@/components/customAttributesV2/FormulaDropdownItem.vue";

import type { EditorNode } from "@/models/TextEditor";
import type {
  CustomAttributeDropdownOption,
  FilterFunction,
} from "@/models/customAttributes/searchSelectDropdown";
import type { FormulaOption } from "@/models/options";

const emit = defineEmits<{
  "node:select": [CustomAttributeDropdownOption];
}>();

const props = defineProps<{
  node: EditorNode;
  argumentOptions: Record<string, string>;
}>();

const { t } = useI18n();
const { getters } = useStore();
const { serviceAttributes, getServiceProviderDetails } =
  useAttributeBuilderServices();
const searchTerm = ref("");

const optionsListRef = ref<HTMLElement | null>(null);

const functionOptions = computed<FormulaOption[]>(
  () => getters["options/attributeFunctions"]
);

const moreItemsCountText = computed(
  () =>
    `${t("COMMON.AND")} ${
      foundItemsCount.value - filteredItems.value.length
    } ${t("COMMON.MORE").toLowerCase()}`
);

const serviceAttributesOptions = computed(() => {
  return serviceAttributes.value.map((attr) => {
    const provider = getServiceProviderDetails(attr.id);
    return {
      name: attr.name,
      label: [provider?.title, provider?.name, provider?.category]
        .filter(Boolean)
        .join(" / "),
      type: EditorNodeName.attribute,
      id: attr.id,
      ...((attr.options?.month_picker_range ||
        attr.options?.consumer_score_models_levels) && {
        context: undefined,
      }),
      ...(attr.context && { context: attr.context }),
    };
  });
});

const servicesWithFunctions = computed(() => {
  return [
    ...Object.entries(
      props.argumentOptions ?? {}
    ).map<CustomAttributeDropdownOption>(([name, label]) => ({
      name,
      label,
      type: EditorNodeName.attributeOption,
    })),
    ...functionOptions.value.map<CustomAttributeDropdownOption>((option) => ({
      name: option.name,
      aliases: option.aliases,
      label: option.usage || undefined,
      type: EditorNodeName.function,
    })),
    ...serviceAttributesOptions.value,
  ];
});

const filterFunction: FilterFunction<CustomAttributeDropdownOption> = (
  needle,
  items
) => {
  return items.filter(({ aliases, label, name }) =>
    `${label}${name}${aliases?.join("")}`
      .toLowerCase()
      .includes(needle.toLowerCase())
  );
};

const {
  isOpen,
  filteredItems,
  foundItemsCount,
  highlightedIndex,
  open,
  closeAndClear,
  select,
  selectHighlighted,
  highlightPrevious,
  highlightNext,
  onSelected,
  onClose,
} = useSearchSelectProvider(
  {
    needle: searchTerm,
    items: servicesWithFunctions,
    filterFunction,
  },
  {
    initialHighlightedIndex: 0,
    scrollRef: optionsListRef,
    slice: 20,
  }
);
const search = (value: string) => {
  if (!value?.trim()) {
    closeAndClear();
    return;
  }
  open();
  searchTerm.value = value;
};

onSelected((option) => {
  emit("node:select", option);
  closeAndClear();
});

onClose(() => {
  searchTerm.value = "";
});

const arrowHandlers = {
  ArrowUp: highlightPrevious,
  ArrowDown: highlightNext,
  Enter: selectHighlighted,
};

onKeyStroke(Object.keys(arrowHandlers), (e) => {
  e.preventDefault();
  arrowHandlers[e.key as keyof typeof arrowHandlers]();
});

watch(
  () => props.node,
  (newValue, oldValue) => {
    if (newValue?.content === oldValue?.content) {
      return;
    }
    if (
      newValue?.content?.trim() &&
      [EditorNodeName.uncategorized, EditorNodeName.unrecognized].includes(
        newValue.type
      )
    ) {
      search(newValue.content);
    } else {
      closeAndClear();
    }
  }
);
</script>
